/**
 * The eight modules of the IP journey, plus the Education Center. Used by
 * the sidebar and the locked-module preview pages.
 */

export interface ModuleSpec {
  num: string;
  slug: string;
  title: string;
  shortTitle: string;
  description: string;
  status: 'live' | 'preview';
  pricingNote: string;
  // True when this stage is part of the pay-per-invention pricing tier.
  // Independent from `status` — the Prior Art Search Engine is `live` and
  // actively priced per invention (the $14.99 Search Strategy Plan and the
  // $499.99 Full Automated Search). Drives the position of the
  // "PAY-PER-INVENTION BEGINS BELOW" divider on the landing-page
  // journey timeline: divider sits before the first stage where this
  // is true.
  payPerInvention?: boolean;
  rationale?: string;
  href: string;
}

export const JOURNEY_MODULES: ModuleSpec[] = [
  {
    num: '01',
    slug: 'capture',
    title: 'Idea Capture',
    shortTitle: 'Capture',
    description:
      'Capture every idea you have, with version control built in. Keep your evolving concepts safe and queryable.',
    status: 'live',
    pricingNote: 'Free, always.',
    href: '/ideas',
  },
  {
    num: '02',
    slug: 'strategy',
    title: 'Strategy Coaching',
    shortTitle: 'Strategy',
    description:
      'A guided session with your personal advisory board — IP consultant, product strategist, and financial analyst — ending in an invention strategy memo with real prior art and a verdict you can take to your attorney meeting.',
    status: 'live',
    pricingNote: 'Free, always.',
    href: '/strategy',
  },
  {
    num: '03',
    // Slug stays `patent-search-engine` so existing routes, the roadmap
    // file, waitlist entries, and bookmarks keep resolving. Only the
    // user-facing name changes — "prior art search" is more accurate
    // than "patent search" (we also cover non-patent academic
    // literature in Phase 2) and matches the term USPTO examiners use.
    slug: 'patent-search-engine',
    title: 'Prior Art Search Engine',
    shortTitle: 'Search',
    description:
      'Build a complete prior-art search strategy for your idea — classifications, keyword sets, Boolean queries, citation and assignee expansion plans, and a recommended database sequence, then run it live. The Search Strategy Plan is $14.99 per invention; the Full Automated Search is $499.99 per invention, with the Plan bundled in.',
    status: 'live',
    payPerInvention: true,
    pricingNote:
      'Search Strategy Plan: $14.99 per invention. Full Automated Search: $499.99 per invention, bundles the $14.99 Plan. Both available to all subscription tiers.',
    rationale:
      "The Search Strategy Plan turns the strategy memo into a complete prior-art search plan an attorney would charge billable hours to produce. The Full Automated Search runs those searches live against real patent databases and the academic literature.",
    href: '/search-strategy',
  },
  {
    num: '04',
    slug: 'pathway-navigator',
    title: 'Pathway Navigator',
    shortTitle: 'Pathway',
    description:
      'A guided filing-strategy session: compare the US, Canadian, UK and European patent offices and international routes for your invention, with an AI filing strategist and a dated Pathway Strategy to take to a practitioner. Plus design, trademark, copyright and trade secret strategy.',
    status: 'live',
    payPerInvention: true,
    pricingNote: '$499.99 per invention, with 5 strategy updates a year included.',
    rationale:
      'The Pathway Navigator generates a full procedural plan tailored to your idea, jurisdiction strategy, and budget, and sets out your wider IP strategy: whether to protect a brand name with a trademark (with a pointer to the USPTO\'s free trademark search), what copyright covers, and when a trade secret is the better choice.',
    href: '/pathway',
  },
  {
    num: '05',
    slug: 'drafting-workbench',
    title: 'Drafting Workbench',
    shortTitle: 'Drafting',
    description:
      'Write your own first patent filing for the US, UK, Canada or Europe. AI drafts each section from your idea, a claims builder checks each claim against your prior art, and a full check scores the draft against the office\u2019s requirements. You file it yourself.',
    status: 'live',
    payPerInvention: true,
    pricingNote: 'Prepare My Filing: $499.99 per invention, all four offices.',
    rationale:
      'Drafting is where most of the cost in a real filing lives. The workbench gives you a complete, office-formatted first draft to file yourself or take to a practitioner.',
    href: '/drafting',
  },
  {
    num: '06',
    slug: 'cost-pipeline',
    title: 'Cost & Filing Manager',
    shortTitle: 'Filing',
    description:
      'Every invention\u2019s fees, deadlines and filing status in one place: a board from idea to grant, deadlines worked out from what you filed with reminders before each one, each office\u2019s filing guide and links, your filing document and a checklist.',
    status: 'live',
    payPerInvention: true,
    pricingNote: 'Included with every Prep Plan. Annual Plus and Pro include 1 and 5 inventions; otherwise from $59.99 per invention.',
    rationale:
      'It saves the spreadsheets and the lookups. It is not a docketing service: confirm deadlines with your patent attorney or agent.',
    href: '/manager',
  },
];

/**
 * Knowledge Vault — its own top-level sidebar category, distinct from
 * the My Invention Log track. Where the Log is the dated, append-only
 * RECORD of how an idea evolved, the Vault is an active INTELLIGENCE
 * and IDEATION workspace: it helps inventors generate new variations on
 * their ideas, explore adjacent markets, build deep knowledge on the
 * topics behind their inventions, and track the news and trends shaping
 * their field. Used across the whole journey, not at any single stage.
 *
 * Slug stays `knowledge-base` so the roadmap file
 * (`src/roadmap/knowledge-base.md`), waitlist entries, and any
 * bookmarks keep resolving. Only the user-facing vision changed.
 */
export const KNOWLEDGE_MODULE = {
  slug: 'knowledge-base',
  title: 'Knowledge Vault',
  shortTitle: 'Vault',
  description:
    'An intelligence and ideation workspace: spin new variations on your ideas, explore adjacent markets, build deep knowledge on the topics behind your inventions, and track the news and trends shaping your field.',
  status: 'preview' as const,
  pricingNote: 'Will be included with subscription.',
  rationale:
    'An active research surface that helps inventors expand and sharpen their thinking — generating idea variations, surfacing adjacent markets, building topic expertise, and keeping a finger on relevant trends. Used across the whole journey, not at any single stage.',
  href: '/modules/knowledge-base',
};

/**
 * Commercialization — its own top-level sidebar category. Sits outside
 * the IP Journey because it begins AFTER the journey ends (post-grant)
 * and operates on a different cadence (licensing deals, royalty tracking,
 * acquirer outreach — months-to-years, not weeks).
 */
export const COMMERCIALIZATION_MODULE = {
  slug: 'commercialization',
  title: 'Commercialization',
  shortTitle: 'Commercialize',
  description:
    'Turn a validated invention into income. License it to a company, build and sell it yourself, or list it for a buyer, with licensing and royalty tracking that can start while your patent is still pending.',
  status: 'preview' as const,
  pricingNote: 'Will be a mix of subscription and success-based fees.',
  rationale:
    'Commercialization comes after your Build Lab. One front door helps you choose the right path: licensing, launching it yourself, or finding a buyer.',
  href: '/modules/commercialization',
};

/**
 * "My Digital Build Lab" — on the roadmap. Shown to members only as one
 * "On the roadmap" preview (not its own nav section) until it ships. It
 * replaces the two former "coming soon" advisor seats (Solution Architect,
 * Supply Chain Expert), which become chat consultants inside the Build Lab
 * with no verdicts. See src/roadmap/build-lab.md.
 */
export const BUILD_LAB_MODULE = {
  slug: 'digital-build-lab',
  title: 'My Digital Build Lab',
  shortTitle: 'Build Lab',
  description:
    'Where your design becomes something real. A Prototype consultant plans how to build it and which hard problems to solve first. A Production consultant works out your parts list, cost per unit and suppliers. Everything you decide is saved to your design, task board and Invention Log.',
  status: 'preview' as const,
  pricingNote: 'A paid add-on, unlocked once an idea has a Go verdict from your advisory board.',
  rationale:
    'It comes after My Design Workspace and before Commercialization: design it, build and test it, then take it to market.',
  href: '/modules/digital-build-lab',
};

/**
 * "My Design Workspace" — its own top-level sidebar category between the
 * numbered IP Journey and My Learning. A PARALLEL track, not a funnel
 * stage. The section header groups three children, each a facet of
 * turning an idea into a buildable, filable design:
 *
 *   1. Design Decomposition (LIVE) — break the invention into components.
 *   2. Design Sketch (coming soon) — capture/sketch the design and turn
 *      sketches into patent-style figures.
 *   3. Invention Log (coming soon) — the dated record of how the
 *      invention evolved (the evidentiary/timeline layer).
 *
 * Renamed from the earlier single "My Invention Log" entry: the active
 * design work is the center of gravity, so the section is named for the
 * workspace and the dated "log" is demoted to one child among several.
 *
 * Legal framing note for the Invention Log child (later phase): since
 * 2013 the US is first-inventor-to-file, so the log is NOT about "proving
 * you invented first." Its value is corroborating inventorship,
 * trade-secret hygiene, diligence/timeline, and feeding the eventual
 * filing. Tamper-evident claims belong to that later evidentiary phase
 * only. Never overclaim.
 */

// Child 1 — LIVE. The design decomposition workspace. Slug is
// 'design-decomposition' but it routes to the existing /invention-log
// index (route kept to avoid churn; the slug only matters for a
// /modules preview, which a live module never shows).
export const DESIGN_DECOMPOSITION_MODULE = {
  slug: 'design-decomposition',
  title: 'Design Decomposition',
  shortTitle: 'Decomposition',
  description:
    'Break each invention into its components — name, function, materials, and how the parts connect — the working breakdown that becomes your patent drawings and your prototype.',
  status: 'live' as const,
  pricingNote: 'Free to use.',
  rationale:
    'A working design document where inventors decompose an idea into labeled parts with materials and connections. The structured foundation that later feeds patent drawings and prototyping.',
  href: '/invention-log',
};

// Child 2 — coming soon. Capture a sketch (upload, photo, or draw) and
// turn it into clean patent-style figures, with the decomposition's
// component numbers as the figure callouts.
export const DESIGN_SKETCH_MODULE = {
  slug: 'design-sketch',
  title: 'Design Sketch',
  shortTitle: 'Sketch',
  description:
    'Turn your parts list into a plain drawing brief for whoever draws it, then into numbered sketches of your invention. The brief is free on every plan.',
  status: 'live' as const,
  pricingNote: 'The drawing brief is free on every plan.',
  rationale:
    'The visual half of the design workspace. Step one is a confirmed, part-by-part drawing brief drafted from the decomposition; numbered sketches are drawn from it.',
  href: '/design-sketch',
};

// Child 3 — coming soon. The dated record/timeline of how the invention
// evolved. The evidentiary layer (mutable now, tamper-evident later).
export const INVENTION_LOG_MODULE = {
  slug: 'invention-log',
  title: 'Invention Log',
  shortTitle: 'Log',
  description:
    'A dated record of your work on each idea: what you tried, what happened and what comes next. Print it free, or download a PDF with a subscription.',
  status: 'live' as const,
  pricingNote: 'Free to keep and print. PDF download with a subscription.',
  rationale:
    'The timeline/record facet of the design workspace: a contemporaneous log of how an invention developed. Its evidentiary value (corroborating inventorship, diligence, trade-secret hygiene) firms up in a later phase; never overclaims tamper-evidence on the mutable working version.',
  href: '/log',
};

export const LEARN_MODULE = {
  slug: 'learn',
  title: 'Education Center',
  shortTitle: 'Learn',
  description:
    'Daily IP knowledge snippets, structured learning paths, lessons, quizzes, streaks, and badges.',
  status: 'live' as const,
  pricingNote: 'Subscription unlocks the full library.',
  href: '/learn',
};

/**
 * Practitioner Marketplace — its own top-level category in the sidebar,
 * deliberately separated from the 8-stage IP Journey. The marketplace is
 * a tool you use AT a stage (when ready to engage outside counsel),
 * not a stage itself — so it shouldn't be numbered alongside Capture
 * or Strategy. Lives below "My Learning" in the sidebar.
 */
export const MARKETPLACE_MODULE = {
  slug: 'practitioner-marketplace',
  title: 'Practitioner Marketplace',
  shortTitle: 'Practitioners',
  description:
    'Access over 50,000 USPTO-registered patent attorneys and agents — every profile enriched with firm websites, practice focus, technical specialties, and accessibility signals. Filter by state, specialty, and whether they accept micro-entity inventors.',
  status: 'live' as const,
  pricingNote: 'Free to browse 3 cards per state. Subscription unlocks the full marketplace.',
  href: '/marketplace',
};

/**
 * Prototyping Marketplace — a second marketplace category, placed
 * directly beneath the Practitioner Marketplace. Where the Practitioner
 * Marketplace connects inventors with patent attorneys and agents, this
 * connects them with the shops that turn an idea into a physical
 * prototype: 3D printing, CNC machining, injection molding, PCB
 * fabrication, and product-design studios. Like the practitioner
 * directory, it's a tool used AT a stage (when ready to build), not a
 * journey stage itself. Coming soon: renders locked and routes to the
 * module preview page until the directory is built.
 */
export const PROTOTYPING_MODULE = {
  slug: 'prototyping-marketplace',
  title: 'Prototyping Marketplace',
  shortTitle: 'Prototyping',
  description:
    'A curated directory of prototyping partners — 3D printing, CNC machining, injection molding, PCB fabrication, and product-design shops — matched to your invention by process, material, location, and budget.',
  status: 'preview' as const,
  pricingNote: 'Free to browse a sample; subscription unlocks the full directory.',
  rationale:
    'Once an idea is protectable, the next real-world hurdle is building a working prototype. A vetted marketplace of prototyping providers — filtered by process, material, location, and budget — turns "I have an idea" into "I have something I can hold."',
  href: '/modules/prototyping-marketplace',
};

/**
 * Find a module by slug. Searches across the journey AND the
 * standalone categories (Education, Marketplace, Knowledge Vault,
 * Commercialization) so the `/modules/:slug` preview page can still
 * resolve any module after the sidebar restructure that moved
 * Knowledge Vault and Commercialization out of JOURNEY_MODULES.
 *
 * Returns a uniform shape so the preview page doesn't need to know
 * which category a module came from. Standalone modules don't have
 * a `num` (stage number), so we default to '—' for those.
 */
export function findModule(slug: string): ModuleSpec | undefined {
  const fromJourney = JOURNEY_MODULES.find((m) => m.slug === slug);
  if (fromJourney) return fromJourney;

  // Map standalone modules into the ModuleSpec shape. None of them
  // are numbered journey stages, so we give them placeholder nums
  // that the preview page can render as a generic glyph.
  const standalones: Array<typeof KNOWLEDGE_MODULE | typeof COMMERCIALIZATION_MODULE | typeof BUILD_LAB_MODULE | typeof LEARN_MODULE | typeof MARKETPLACE_MODULE | typeof DESIGN_DECOMPOSITION_MODULE | typeof DESIGN_SKETCH_MODULE | typeof INVENTION_LOG_MODULE | typeof PROTOTYPING_MODULE> = [
    KNOWLEDGE_MODULE,
    BUILD_LAB_MODULE,
    COMMERCIALIZATION_MODULE,
    LEARN_MODULE,
    MARKETPLACE_MODULE,
    DESIGN_DECOMPOSITION_MODULE,
    DESIGN_SKETCH_MODULE,
    INVENTION_LOG_MODULE,
    PROTOTYPING_MODULE,
  ];
  const standalone = standalones.find((m) => m.slug === slug);
  if (standalone) {
    return {
      num: '—',
      slug: standalone.slug,
      title: standalone.title,
      shortTitle: standalone.shortTitle,
      description: standalone.description,
      status: standalone.status,
      pricingNote: standalone.pricingNote,
      rationale: (standalone as { rationale?: string }).rationale ?? '',
      href: standalone.href,
    };
  }

  return undefined;
}
